import React, { useState } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  Platform,
  type ViewStyle,
} from 'react-native';

import { formFieldInStack } from './formFieldStackContext';
import {
  EMPTY_FIELD_DISPLAY,
  ViewOnlyFieldValue,
  resolveCheckboxViewOnlyDisplayText,
  resolveIsViewOnlyPresentation,
} from './viewOnlyFieldUtils';
import { useFormPermissions } from '../../hooks/useFormPermissions';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, fontWeight, lineHeight } from '../../theme/typography';
import { Check } from '../icons';

export interface FormCheckboxProps {
  label: string;
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
  /** When false with `disabled`, keeps legacy read-only chrome instead of view-only text. */
  editable?: boolean;
  /** View-only request mode: plain Yes/No / dash instead of checkbox chrome. */
  readOnlyMode?: boolean;
  /** When true, shows indeterminate dash; parent should clear when toggling. */
  indeterminate?: boolean;
  testID?: string;
  /** Merged with the outer field wrapper, e.g. `marginBottom: 0` when the checkbox is in a grid row. */
  containerStyle?: ViewStyle;
}

const OUT = spacing.formCheckboxOuterSize;
const OUT_DISABLED = spacing.formCheckboxOuterDisabledSize;
const INNER = spacing.formCheckboxInnerSize;
const HALO_PAD = 3;

function FormCheckboxIndeterminateDash({ color }: { color: string }) {
  return (
    <View
      style={[
        styles.indeterminateDash,
        {
          width: spacing.formCheckboxIndeterminateDashWidth,
          height: spacing.formCheckboxIndeterminateDashHeight,
          backgroundColor: color,
        },
      ]}
    />
  );
}

/**
 * Boolean checkbox (optional indeterminate): label first (reading order), then the box. Entire row is tappable.
 * Visual Template checkbox `1855:7965` — selection blue fill / stroke, white mark, disabled grey, web interaction halos.
 */
export const FormCheckbox: React.FC<FormCheckboxProps> = ({
  label,
  value,
  onValueChange,
  disabled = false,
  editable = true,
  readOnlyMode = false,
  indeterminate = false,
  testID,
  containerStyle,
}) => {
  const { canEditRequest } = useFormPermissions();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pressed, setPressed] = useState(false);

  const isPermissionLocked = !disabled && !readOnlyMode && !canEditRequest;
  const isControlDisabled = disabled || readOnlyMode || isPermissionLocked;
  const isViewOnlyPresentation = resolveIsViewOnlyPresentation({
    readOnlyMode,
    disabled,
    editable,
    canEditRequest,
  });

  const onPress = () => {
    if (isControlDisabled) {
      return;
    }
    onValueChange(!value);
  };

  const showCheckedMark = value && !indeterminate;
  const showIndeterminateMark = indeterminate;
  const selectionBlue = colors.inputBorderInteractive;

  const webRing =
    Platform.OS === 'web' && !isControlDisabled
      ? pressed
        ? colors.formCheckboxPressedRing
        : focused
        ? colors.formCheckboxFocusRing
        : hovered
        ? colors.formCheckboxHoverRing
        : undefined
      : undefined;

  const webHalo = webRing ? { boxShadow: webRing } : {};

  const outer = isControlDisabled ? OUT_DISABLED : OUT;
  const boxRadius = isControlDisabled
    ? spacing.formCheckboxDisabledRadius
    : spacing.formCheckboxOuterRadius;

  const accessibilityChecked: boolean | 'mixed' = indeterminate
    ? 'mixed'
    : value;

  if (isViewOnlyPresentation) {
    const displayText = indeterminate
      ? EMPTY_FIELD_DISPLAY
      : resolveCheckboxViewOnlyDisplayText(value);
    return (
      <ViewOnlyFieldValue
        label={label}
        displayText={displayText}
        testID={testID}
        valueTestID={testID ? `${testID}-value` : 'view-only-field-value'}
        containerStyle={[containerStyle, formFieldInStack]}
      />
    );
  }

  return (
    <View style={[styles.formGroup, containerStyle]} testID={testID}>
      <Pressable
        accessibilityRole="checkbox"
        accessibilityState={{
          checked: accessibilityChecked,
          disabled: isControlDisabled,
        }}
        accessibilityLabel={label}
        disabled={isControlDisabled}
        onPress={onPress}
        onPressIn={() => !isControlDisabled && setPressed(true)}
        onPressOut={() => setPressed(false)}
        {...(Platform.OS === 'web' && !isControlDisabled
          ? {
              onHoverIn: () => setHovered(true),
              onHoverOut: () => setHovered(false),
              onFocus: () => setFocused(true),
              onBlur: () => setFocused(false),
              tabIndex: 0 as number,
            }
          : {})}
        style={({ pressed: rowPressed }) => [
          styles.row,
          isControlDisabled && styles.rowDisabled,
          rowPressed &&
            !isControlDisabled &&
            Platform.OS !== 'web' &&
            styles.rowPressed,
        ]}
      >
        <Text style={[styles.label, isControlDisabled && styles.labelDisabled]}>
          {label}
        </Text>
        <View
          style={[
            styles.haloWrap,
            {
              width: outer + HALO_PAD * 2,
              height: outer + HALO_PAD * 2,
              borderRadius: boxRadius + HALO_PAD,
            },
            Platform.OS === 'web' && !isControlDisabled ? webHalo : {},
            Platform.OS !== 'web' && pressed && !isControlDisabled
              ? styles.haloWrapNativePressed
              : {},
          ]}
        >
          <View
            style={[styles.boxAligner, { width: outer, height: outer }]}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            {isControlDisabled ? (
              <View
                style={[
                  styles.boxDisabledBase,
                  {
                    width: OUT_DISABLED,
                    height: OUT_DISABLED,
                    borderRadius: spacing.formCheckboxDisabledRadius,
                  },
                ]}
              >
                {showCheckedMark ? (
                  <Check
                    size={11}
                    color={colors.textFormDisabled}
                    strokeWidth={2.2}
                  />
                ) : showIndeterminateMark ? (
                  <FormCheckboxIndeterminateDash
                    color={colors.textFormDisabled}
                  />
                ) : null}
              </View>
            ) : showCheckedMark || showIndeterminateMark ? (
              <View
                style={[
                  styles.boxChecked,
                  {
                    width: OUT,
                    height: OUT,
                    borderRadius: spacing.formCheckboxOuterRadius,
                    backgroundColor: selectionBlue,
                    borderColor: selectionBlue,
                  },
                ]}
              >
                {showCheckedMark ? (
                  <Check size={13} color={colors.textLight} strokeWidth={2.5} />
                ) : (
                  <FormCheckboxIndeterminateDash color={colors.textLight} />
                )}
              </View>
            ) : (
              <View
                style={[
                  styles.boxUnchecked,
                  {
                    width: INNER,
                    height: INNER,
                    borderRadius: spacing.formCheckboxInnerRadius,
                    borderWidth: spacing.formCheckboxUncheckedBorderWidth,
                    borderColor: selectionBlue,
                  },
                ]}
              />
            )}
          </View>
        </View>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: spacing.formFieldGroupGap,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch' as const,
    width: '100%' as const,
    minWidth: 0,
    justifyContent: 'flex-start' as const,
    gap: spacing.formCheckboxLabelGap,
    ...Platform.select({
      web: {
        cursor: 'pointer' as const,
        userSelect: 'none' as const,
        outlineStyle: 'none' as const,
      },
      default: {},
    }),
  },
  rowDisabled: Platform.select({
    web: { cursor: 'not-allowed' as const },
    default: {},
  }),
  rowPressed: {
    opacity: 0.92,
  },
  haloWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  },
  haloWrapNativePressed: {
    opacity: 0.85,
  },
  boxAligner: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  boxUnchecked: {
    backgroundColor: colors.backgroundPrimary,
  },
  boxChecked: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0,
  },
  boxDisabledBase: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.backgroundFormDisabled,
    borderWidth: 1,
    borderColor: colors.inputBorderDisabled,
  },
  indeterminateDash: {
    borderRadius: 1,
  },
  label: {
    flexGrow: 0,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%' as const,
    fontSize: fontSize.body,
    lineHeight: lineHeight.body,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
  },
  labelDisabled: {
    color: colors.textFormDisabled,
  },
});
